// Shared per-question grading logic for reading/listening practice, used by
// in-part score badges, hub score badges, and saved practice-attempt history.

import { isTableGapQuestion, isTableGapCorrect, tableRowCredit } from './tableAnswers';
import { isNotesPart, isNoteCorrect } from './notesAnswers';
import { isSpellingLenientPart, isGapAnswerLenient } from './gapAnswers';

export function normalizeTF(v) {
  if (v === true || v === 'Richtig' || v === 'R' || String(v).toLowerCase() === 'richtig') return 'richtig';
  if (v === false || v === 'Falsch' || v === 'F' || String(v).toLowerCase() === 'falsch') return 'falsch';
  return String(v || '').toLowerCase();
}

export function isTFCorrect(user, correct) {
  return normalizeTF(user) === normalizeTF(correct);
}

export function isReadingQuestionCorrect(question, userAnswer, part = null) {
  // ÖSD B2 Lesen 3/4: spelling slips that keep the word's meaning count as right.
  if (isSpellingLenientPart(part)) return isGapAnswerLenient(question.correctAnswer, userAnswer);
  // A list means several accepted answers; typed gap answers ignore case and spaces.
  if (Array.isArray(question.correctAnswer) || question.blankNumber != null) {
    const norm = (v) => String(v ?? '').trim().toLowerCase();
    const accepted = Array.isArray(question.correctAnswer) ? question.correctAnswer : [question.correctAnswer];
    return userAnswer != null && accepted.some((a) => norm(a) === norm(userAnswer));
  }
  return userAnswer === question.correctAnswer;
}

export function isListeningQuestionCorrect(question, userAnswer, partQuestionType, part = null) {
  if (isTableGapQuestion(question)) return isTableGapCorrect(question, userAnswer);
  // ÖSD C1 Hören notes: key alternatives "a / b", spelling does not count.
  if (isNotesPart(part)) return isNoteCorrect(question.correctAnswer, userAnswer);
  const qType = question.questionType || partQuestionType;
  if (qType === 'multiple-choice' || qType === 'matching') {
    return userAnswer === question.correctAnswer;
  }
  if (qType === 'fill-blank') {
    const correctArr = Array.isArray(question.correctAnswer) ? question.correctAnswer : [question.correctAnswer];
    return correctArr.some(ca => String(userAnswer || '').trim().toLowerCase() === String(ca).trim().toLowerCase());
  }
  return isTFCorrect(userAnswer, question.correctAnswer);
}

function gradePart(part, answers, isCorrectFn) {
  if (!part) return { correct: 0, total: 0, results: [] };
  const qs = (part.questions || []).filter(q => !q.isExample);
  const results = qs.map(q => {
    const userAnswer = answers[q.questionId];
    const isCorrect = isCorrectFn(q, userAnswer);
    return {
      questionId: q.questionId,
      questionNumber: q.questionNumber,
      questionType: q.questionType || part.questionType,
      userAnswer,
      correctAnswer: q.correctAnswer,
      isCorrect,
      // Table rows (ÖSD B2 Hören Teil 2) earn partial credit per correct cell.
      credit: isTableGapQuestion(q) ? tableRowCredit(q, userAnswer) : (isCorrect ? 1 : 0),
      explanation: q.explanation,
    };
  });
  const correct = Math.round(results.reduce((sum, r) => sum + r.credit, 0) * 100) / 100;
  return { correct, total: qs.length, results };
}

export function gradeReadingPart(part, answers) {
  return gradePart(part, answers, (q, userAnswer) => isReadingQuestionCorrect(q, userAnswer, part));
}

export function gradeListeningPart(part, answers) {
  return gradePart(part, answers, (q, userAnswer) => isListeningQuestionCorrect(q, userAnswer, part.questionType, part));
}
